/**
 * Authentication Service for Wadakaroo
 * Handles JWT token storage, retrieval, and management.
 *
 * JWT token is the single source of truth for user data.
 * User data is decoded from token claims, not stored separately.
 */

const TOKEN_KEY = 'wadakaroo_accessToken';
const REFRESH_TOKEN_KEY = 'wadakaroo_refreshToken';
const AUTH_KEY = 'wadakaroo_auth';

// Cross-tab session management keys.
// LAST_ACTIVITY_KEY: shared timestamp (ms) of the most recent user interaction
//                   across all tabs. Written by useSessionManager, read by every
//                   tab to decide whether to fire the inactivity warning.
// LOGOUT_BROADCAST_KEY: a write-only signal — any tab bumping its value tells
//                   all other tabs to log out.
export const LAST_ACTIVITY_KEY = 'wadakaroo_lastActivityAt';
export const LOGOUT_BROADCAST_KEY = 'wadakaroo_logoutBroadcast';

// Re-export for hooks that need to listen for token changes via the
// `storage` event. Centralised here so the key strings live in one place.
export const ACCESS_TOKEN_STORAGE_KEY = TOKEN_KEY;
export const REFRESH_TOKEN_STORAGE_KEY = REFRESH_TOKEN_KEY;

// ============================================
// Token Decoding
// ============================================

/**
 * Decode JWT token payload (without verification).
 * Signature verification happens server-side. This just extracts claims.
 */
export const decodeToken = (token) => {
    if (!token) return null;

    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        return payload;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Parse JWT token and extract user info.
 * This is the single source of truth for user data.
 *
 * NOTE: When adding new claims in WadakarooClaimsGenerator on the backend,
 * remember to surface them here too — otherwise they get dropped before
 * reaching Redux.
 */
export const parseToken = (token) => {
    const payload = decodeToken(token);
    if (!payload) return null;

    const roles = payload.roles || [];
    const permissions = payload.permissions || [];

    // organisationSetupRequired: false means org IS provisioned
    // organisationSetupRequired: true (or undefined) means org is NOT provisioned
    const organisationSetupRequired = payload.organisationSetupRequired;
    const organisationProvisioned = organisationSetupRequired === false;

    // If user has an organisationUuid and org setup is not required, registration is complete
    const registrationComplete = !!payload.organisationUuid || organisationProvisioned;

    return {
        // Identity
        email: payload.email || payload.sub,
        userUuid: payload.userUuid,

        // Type and Role
        userType: payload.userType,
        staffRole: payload.staffRole,
        role: payload.role,

        // UUIDs
        externalUuid: payload.externalUuid,
        organisationUuid: payload.organisationUuid,
        staffUuid: payload.staffUuid,

        // Business type — MECHANIC_WORKSHOP / CAR_WASH.
        //
        // ⚠️ Must stay mapped. The backend has always put this claim in the JWT, but it
        //    was missing from this object, so `user.organisationType` read undefined
        //    everywhere and any feature gated on it silently never rendered — the Repco
        //    SmartQuote import button in JobDetailModal was invisible to every workshop,
        //    which looked like the feature had been switched off rather than a claim
        //    never being read.
        organisationType: payload.organisationType,

        // Fleet portal scope for CLIENT users — the customer a fleet login is limited
        // to. Mapped for the same reason: the claim exists server-side and anything
        // reading it off the user object needs it present.
        customerUuid: payload.customerUuid,

        // Permissions
        permissions: permissions,
        roles: roles,

        // Staff permission profile
        profileUuid: payload.profileUuid,
        profileName: payload.profileName,

        // UI views — drives Layout.jsx nav rendering.
        // OWNER receives every UiViewKey via the backend short-circuit;
        // STAFF/CLIENT receive the keys from their assigned UiViewProfile.
        uiViews: payload.uiViews || [],
        uiViewProfileUuid: payload.uiViewProfileUuid,
        uiViewProfileName: payload.uiViewProfileName,

        // Profile flags - derived from JWT claims
        registrationComplete: registrationComplete,
        organisationProvisioned: organisationProvisioned,
        organisationSetupRequired: organisationSetupRequired,
        passwordResetRequired: payload.passwordResetRequired || false,

        // Token metadata
        tokenScope: payload.tokenScope,
        exp: payload.exp,
        iat: payload.iat,
        expiresAt: payload.exp ? payload.exp * 1000 : null,
        issuedAt: payload.iat ? payload.iat * 1000 : null,
    };
};

// ============================================
// Token Storage
// ============================================

export const setTokens = (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    updateStoredAuth({ accessToken, refreshToken });
};

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

// ============================================
// Auth Data Storage
// ============================================

export const getStoredAuth = () => {
    try {
        const stored = localStorage.getItem(AUTH_KEY);
        if (!stored) return null;
        return JSON.parse(stored);
    } catch (error) {
        console.error('Failed to get stored auth:', error);
        return null;
    }
};

export const storeAuth = (authData) => {
    try {
        localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
    } catch (error) {
        console.error('Failed to store auth:', error);
    }
};

const updateStoredAuth = (updates) => {
    try {
        const stored = getStoredAuth() || {};
        storeAuth({ ...stored, ...updates });
    } catch (error) {
        console.error('Failed to update stored auth:', error);
    }
};

export const setUser = (user) => {
    if (user) {
        updateStoredAuth({ user });
        if (user.externalUuid || user.userUuid) {
            localStorage.setItem('wadakaroo_userUuid', user.externalUuid || user.userUuid);
        }
        if (user.email) {
            localStorage.setItem('wadakaroo_userEmail', user.email);
        }
        if (user.organisationUuid) {
            localStorage.setItem('wadakaroo_organisationUuid', user.organisationUuid);
        }
    }
};

export const getUser = () => {
    const token = getAccessToken();
    if (token) {
        const tokenUser = parseToken(token);
        if (tokenUser) {
            const stored = getStoredAuth();
            return { ...stored?.user, ...tokenUser };
        }
    }
    const stored = getStoredAuth();
    return stored?.user || null;
};

// ============================================
// Auth Lifecycle
// ============================================

export const clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem('wadakaroo_userUuid');
    localStorage.removeItem('wadakaroo_userEmail');
    localStorage.removeItem('wadakaroo_organisationUuid');
    // Session-tracking keys: drop them too so a stale "last active" stamp
    // from a previous session doesn't suppress the inactivity timer on
    // the next login.
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    console.log('🧹 Auth data cleared from localStorage');
};

export const isAuthenticated = () => {
    const token = getAccessToken();
    if (!token) return false;

    try {
        const payload = decodeToken(token);
        if (!payload || !payload.exp) return false;
        return Date.now() < payload.exp * 1000;
    } catch (error) {
        console.error('Error checking authentication:', error);
        return false;
    }
};

export const getTokenExpiration = () => {
    const token = getAccessToken();
    if (!token) return null;
    try {
        const payload = decodeToken(token);
        return payload?.exp ? payload.exp * 1000 : null;
    } catch (error) {
        return null;
    }
};

export const shouldRefreshToken = (bufferSeconds = 300) => {
    const expiration = getTokenExpiration();
    if (!expiration) return false;
    return Date.now() > (expiration - bufferSeconds * 1000);
};

export const isTokenExpired = (token = null, bufferSeconds = 60) => {
    const targetToken = token || getAccessToken();
    if (!targetToken) return true;
    try {
        const payload = decodeToken(targetToken);
        if (!payload || !payload.exp) return true;
        return Date.now() >= (payload.exp * 1000 - bufferSeconds * 1000);
    } catch (error) {
        return true;
    }
};

// ============================================
// Activity Tracking (cross-tab)
// ============================================

/**
 * Read the most recent activity timestamp shared across tabs.
 * Returns Date.now() if nothing has been recorded yet, so a freshly
 * authenticated tab doesn't immediately think the user is idle.
 */
export const getLastActivity = () => {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return Date.now();
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : Date.now();
};

/**
 * Record an activity timestamp. Writing to localStorage also fires a
 * `storage` event in OTHER tabs, which is how cross-tab activity sync
 * works without needing a BroadcastChannel.
 */
export const setLastActivity = (timestamp = Date.now()) => {
    try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
    } catch (error) {
        console.warn('Failed to record activity timestamp:', error);
    }
};

/**
 * Broadcast a logout intent to all other tabs. Writing a fresh value
 * (not just removing the key) guarantees the storage event fires even
 * when the previous value was the same.
 */
export const broadcastLogout = (reason = 'manual') => {
    try {
        localStorage.setItem(LOGOUT_BROADCAST_KEY, `${reason}:${Date.now()}`);
    } catch (error) {
        console.warn('Failed to broadcast logout:', error);
    }
};

// ============================================
// Permission Helpers
// ============================================

export const hasPermission = (permission) => {
    const user = getUser();
    return (user?.permissions || []).includes(permission);
};

export const hasAnyPermission = (permissions) => {
    const user = getUser();
    const userPerms = user?.permissions || [];
    return permissions.some(p => userPerms.includes(p));
};

export const hasAllPermissions = (permissions) => {
    const user = getUser();
    const userPerms = user?.permissions || [];
    return permissions.every(p => userPerms.includes(p));
};

// ============================================
// UI View Helpers
// ============================================

/**
 * Check if the current user is allowed to see a given UiViewKey.
 * Mirrors the backend's UiViewProfileService resolution.
 */
export const hasUiView = (uiViewKey) => {
    const user = getUser();
    return (user?.uiViews || []).includes(uiViewKey);
};

// ============================================
// Default Export
// ============================================

const authService = {
    decodeToken, parseToken, setTokens, getAccessToken, getRefreshToken, isTokenExpired,
    getStoredAuth, storeAuth, setUser, getUser, clearAuth,
    isAuthenticated, getTokenExpiration, shouldRefreshToken,
    getLastActivity, setLastActivity, broadcastLogout,
    hasPermission, hasAnyPermission, hasAllPermissions, hasUiView,
};

export default authService;